import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import * as React from 'react';
import { SVGProps } from 'react';

import { cn } from '@/lib/utils';
import Spinner from '../Spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-none focus:outline-none focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'bg-[var(--mb-primary)] text-white hover:bg-[var(--mb-primary-hover)] shadow-[var(--mb-primary-shadow)]',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background text-gray-900 hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        primary: 'bg-[var(--mb-primary)] text-white hover:bg-[var(--mb-primary-hover)] shadow-[var(--mb-primary-shadow)]',
        // Add variants from old Button component
        solid: 'bg-gray-1000 text-white hover:bg-black shadow-sm',
        inline: 'text-gray-1000 hover:text-black',
        info: 'bg-primary-500 text-white hover:bg-primary-600',
        success: 'bg-green-500 text-white hover:bg-green-600',
        warning: 'bg-orange-500 text-white hover:bg-orange-600',
        error: 'bg-red-500 text-white hover:bg-red-600',
        // Add gradient variant for special effects
        gradient: 'bg-[var(--mb-primary)] text-white hover:bg-[var(--mb-primary-hover)] shadow-[var(--mb-primary-shadow)]',
        // Add active/selected state
        active: 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100',
        // Toolbar variant for editor toolbars (gray background when active)
        toolbar: 'bg-gray-300 text-gray-900 hover:bg-gray-300',
        // Dark variant for dark buttons (slate-900)
        dark: 'bg-slate-900 text-white hover:bg-slate-800',
      },
      size: {
        default: 'h-10 px-4 py-2 text-sm md:text-base',
        sm: 'h-9 px-3 text-xs md:text-sm',
        lg: 'h-11 px-8 text-base md:text-lg',
        xl: 'h-12 px-10 text-lg md:text-xl',
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-8 w-8 p-0',
        'icon-lg': 'h-12 w-12 p-0',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-fit',
      },
      rounded: {
        default: 'rounded-lg',
        md: 'rounded-md',
        full: 'rounded-full',
        none: 'rounded-none',
      },
      padding: {
        default: '',
        none: 'p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
      rounded: 'default',
      padding: 'default',
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render as a child component (for Radix UI composition)
   */
  asChild?: boolean;
  /**
   * Show loading spinner and disable button
   */
  isLoading?: boolean;
  /**
   * Icon to display before the button text
   */
  startIcon?: React.ReactNode;
  /**
   * Icon to display after the button text
   */
  endIcon?: React.ReactNode;
  /**
   * Render as a link (internal or external)
   */
  href?: string;
  /**
   * Link target attribute
   */
  target?: string;
  /**
   * Link rel attribute
   */
  rel?: string;
  /**
   * Disable button (prevents interaction)
   */
  disabled?: boolean;
  /**
   * Legacy support - disable padding
   * @deprecated Use padding="none" instead
   */
  disabledpadding?: boolean;
  /**
   * Props for the loading spinner icon
   */
  loadingIconProps?: SVGProps<SVGSVGElement>;
  /**
   * Legacy support - link prop
   * @deprecated Use href instead
   */
  link?: string;
  /**
   * Legacy support - gradient on hover
   * @deprecated Use variant="gradient" instead
   */
  linearOnHover?: boolean;
  /**
   * ARIA label for icon-only buttons (required for accessibility)
   */
  'aria-label'?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      rounded,
      padding,
      asChild = false,
      isLoading = false,
      startIcon,
      endIcon,
      href,
      target,
      rel,
      // Legacy props
      disabledpadding = false,
      loadingIconProps,
      link,
      linearOnHover = false,
      ...props
    },
    ref,
  ) => {
    // Handle legacy props
    const computedPadding = disabledpadding ? 'none' : padding;
    const computedVariant = linearOnHover ? 'gradient' : variant;
    const computedHref = href || link;
    const Comp = asChild ? Slot : 'button';

    // If href is provided, render as a link
    if (computedHref && !asChild) {
      const linkProps = {
        href: computedHref,
        target,
        rel: target === '_blank' ? rel || 'noopener noreferrer' : rel,
      };

      // Use Next.js Link for internal links, anchor for external links
      const isExternal = computedHref.startsWith('http') || computedHref.startsWith('mailto:');

      if (isExternal) {
        return (
          <a
            className={cn(
              buttonVariants({
                variant: computedVariant,
                size,
                fullWidth,
                rounded,
                padding: computedPadding,
                className,
              }),
              (startIcon || endIcon || isLoading) && 'inline-flex items-center gap-2',
            )}
            onClick={props.onClick as unknown as React.MouseEventHandler<HTMLAnchorElement>}
            {...linkProps}
          >
            {isLoading && (
              <Spinner
                width={size === 'sm' ? 16 : 20}
                height={size === 'sm' ? 16 : 20}
                {...loadingIconProps}
              />
            )}
            {startIcon && !isLoading && startIcon}
            {props.children}
            {endIcon && endIcon}
          </a>
        );
      } else {
        return (
          <Link
            className={cn(
              buttonVariants({
                variant: computedVariant,
                size,
                fullWidth,
                rounded,
                padding: computedPadding,
                className,
              }),
              (startIcon || endIcon || isLoading) && 'inline-flex items-center gap-2',
            )}
            href={computedHref}
            target={target}
            rel={rel}
            onClick={props.onClick as unknown as React.MouseEventHandler<HTMLAnchorElement>}
          >
            {isLoading && (
              <Spinner
                width={size === 'sm' ? 16 : 20}
                height={size === 'sm' ? 16 : 20}
                {...loadingIconProps}
              />
            )}
            {startIcon && !isLoading && startIcon}
            {props.children}
            {endIcon && endIcon}
          </Link>
        );
      }
    }

    // Determine if this is an icon-only button (for accessibility)
    const isIconOnly = (size === 'icon' || size === 'icon-sm' || size === 'icon-lg') && !props.children;
    
    // Warn if icon-only button lacks aria-label
    if (isIconOnly && !props['aria-label'] && !props['aria-labelledby']) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'Button: Icon-only buttons should have an aria-label or aria-labelledby for accessibility',
        );
      }
    }

    // Default to 'button' type to prevent form submission unless explicitly set
    const { type: buttonType = 'button', ...restProps } = props;
    
    // When asChild=true the child is rendered via Radix Slot which calls
    // React.Children.only — even falsy siblings (e.g. {isLoading && ...})
    // count toward Children.count, so we must pass props.children directly.
    // The consumer's wrapped element is responsible for its own icons.
    if (asChild) {
      return (
        <Comp
          className={cn(
            buttonVariants({
              variant: computedVariant,
              size,
              fullWidth,
              rounded,
              padding: computedPadding,
              className,
            }),
            (startIcon || endIcon || isLoading) && 'gap-2',
          )}
          ref={ref}
          {...restProps}
          type={buttonType}
          disabled={isLoading || props.disabled}
          aria-disabled={isLoading || props.disabled}
          aria-busy={isLoading}
        >
          {props.children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant: computedVariant,
            size,
            fullWidth,
            rounded,
            padding: computedPadding,
            className,
          }),
          (startIcon || endIcon || isLoading) && 'gap-2',
        )}
        ref={ref}
        {...restProps}
        type={buttonType} // Default to 'button' to prevent form submission (explicit type in props will override)
        disabled={isLoading || props.disabled}
        aria-disabled={isLoading || props.disabled}
        aria-busy={isLoading}
      >
        {isLoading && (
          <Spinner
            width={size === 'sm' || size === 'icon-sm' ? 16 : size === 'lg' || size === 'xl' || size === 'icon-lg' ? 24 : 20}
            height={size === 'sm' || size === 'icon-sm' ? 16 : size === 'lg' || size === 'xl' || size === 'icon-lg' ? 24 : 20}
            {...loadingIconProps}
          />
        )}
        {startIcon && !isLoading && startIcon}
        {props.children}
        {endIcon && endIcon}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
